import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { calculateCumulativeSeries } from "../lib/calc";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NAVY = "rgb(38, 44, 96)";
const RUST = "rgb(221, 63, 10)";
const GRID = "rgba(24, 24, 27, 0.06)";
const AXIS = "rgb(113, 113, 122)";

const FONT_STACK =
  '"Geist Variable", ui-sans-serif, system-ui, sans-serif';

const TimelineChart = ({ events, title }) => {
  const chartData = useMemo(() => {
    const series = calculateCumulativeSeries(events);
    return {
      labels: series.map((item) => item.label),
      datasets: [
        {
          label: "Estimated (cumulative)",
          data: series.map((item) => item.cumulativeEstimated),
          borderColor: RUST,
          backgroundColor: RUST,
          tension: 0.2,
        },
        {
          label: "Actual (cumulative)",
          data: series.map((item) => item.cumulativeActual),
          borderColor: NAVY,
          backgroundColor: NAVY,
          tension: 0.2,
        },
      ],
    };
  }, [events]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false,
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: GRID, drawTicks: false },
        ticks: {
          color: AXIS,
          font: { family: FONT_STACK, size: 11 },
          padding: 8,
          maxTicksLimit: 6,
          callback: (value) => {
            const days = value / 24;
            // Sub-day ranges need more precision; full-day ranges read fine at 1 decimal
            return days < 1 ? days.toFixed(2) : days.toFixed(1);
          },
        },
        title: {
          display: true,
          text: "Cumulative duration (days)",
          color: AXIS,
          font: { family: FONT_STACK, size: 11, weight: "500" },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: AXIS,
          font: { family: FONT_STACK, size: 12 },
          boxWidth: 10,
          boxHeight: 10,
          padding: 16,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: { display: false, text: title || "Project Timeline" },
      tooltip: {
        backgroundColor: "rgba(17, 20, 46, 0.95)",
        titleFont: { family: FONT_STACK, weight: "600" },
        bodyFont: { family: FONT_STACK },
        padding: 10,
        cornerRadius: 6,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 3, hoverRadius: 6, borderWidth: 0 },
    },
  }), [title]);

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="h-72 sm:h-80">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default React.memo(TimelineChart);
